import { ComponentPropsWithoutRef, ElementRef, forwardRef } from 'react';

import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'styles';

import { XIcon } from '../Icons';
import { Column, ColumnProps } from '../Containers';
import { OverlayBackground } from './Background';
import { OverlayHeader } from './Header';
import { OverlayCommonProps } from './types';
import { useToggleV2, useWatch } from 'hooks';
import { safeStateUpdate } from 'utils/react';
import { OVERLAY_BG_IDS } from './escape';

const Sheet = SheetPrimitive.Root;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetTrigger = SheetPrimitive.Trigger;

const sheetVariants = cva(
  'fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out',
  {
    variants: {
      side: {
        top: 'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
        bottom:
          'inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
        left: 'inset-y-0 left-0 h-full w-3/4 max-w-sm border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
        right:
          'inset-y-0 right-0 h-full w-3/4 max-w-sm border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
      },
    },
    defaultVariants: {
      side: 'right',
    },
  },
);

interface SheetContentProps
  extends ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  gap?: ColumnProps['gap'];
  close?: () => void;
  showCloseButton?: boolean;
  preventOpenAutoFocus?: boolean;
}

const SheetContent = forwardRef<ElementRef<typeof SheetPrimitive.Content>, SheetContentProps>(
  (
    {
      side = 'right',
      className,
      children,
      gap,
      close,
      showCloseButton = true,
      preventOpenAutoFocus,
      id,
      ...props
    },
    ref,
  ) => (
    <SheetPortal>
      <OverlayBackground id={id ?? OVERLAY_BG_IDS.sideSheet} />

      <SheetPrimitive.Content
        ref={ref}
        className={cn(sheetVariants({ side }), className)}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onOpenAutoFocus={preventOpenAutoFocus ? (e) => e.preventDefault() : undefined}
        {...props}
      >
        {showCloseButton && (
          <SheetClose
            onClick={close}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary"
          >
            <XIcon size={16} />
            <span className="sr-only">Close</span>
          </SheetClose>
        )}

        <Column gap={gap} fullHeight>
          {children}
        </Column>
      </SheetPrimitive.Content>
    </SheetPortal>
  ),
);

SheetContent.displayName = SheetPrimitive.Content.displayName;

type SideSheetProps = OverlayCommonProps & Pick<SheetContentProps, 'side'>;

export function SideSheet({
  isOpen: controlledIsOpen,
  close,
  children,
  allowEscape = true,
  title,
  description,
  trigger,
  ...props
}: SideSheetProps): JSX.Element {
  const [isOpened, { setIsOn, turnOff, turnOn }] = useToggleV2(controlledIsOpen);

  useWatch(controlledIsOpen, () => (controlledIsOpen ? turnOn() : turnOff()));

  const actualClose = () => {
    setIsOn(false);

    if (close) safeStateUpdate(close);
  };

  const handleOpenChange = (newState: boolean): void => {
    if (newState) return setIsOn(newState);

    if (!allowEscape) return;

    actualClose();
  };

  return (
    <Sheet open={isOpened} onOpenChange={handleOpenChange}>
      <SheetContent close={actualClose} {...props}>
        <OverlayHeader title={title} description={description} />

        {children}
      </SheetContent>

      {trigger && <SheetTrigger asChild>{trigger}</SheetTrigger>}
    </Sheet>
  );
}
